import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Key, Database, Brain, Shield } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default function APIKeysPage() {
  const contentRef = useRef(null);

  const apiKeys = [
    {
      name: 'MongoDB Connection URL',
      key: 'MONGO_URL',
      value: 'mongodb+srv://admin-cv-builder:vJmrvxSnbzshU0EO@cv-builder.ofzllbs.mongodb.net/?retryWrites=true&w=majority',
      icon: Database,
      color: 'bg-green-500'
    },
    {
      name: 'Emergent LLM Key',
      key: 'EMERGENT_LLM_KEY',
      value: 'sk-emergent-55150F6434fBd63Fb2',
      icon: Shield,
      color: 'bg-purple-500'
    },
    {
      name: 'Google Gemini API Key',
      key: 'GOOGLE_GEMINI_API_KEY',
      value: 'AIzaSyAETBCPCslLguHBBVqW1730c65GMwClSGY',
      icon: Brain,
      color: 'bg-blue-500'
    }
  ];

  const downloadPDF = async () => {
    if (!contentRef.current) return;

    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save('EdoMatch_API_Keys.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Download Button */}
        <div className="mb-6 flex justify-end">
          <Button 
            onClick={downloadPDF}
            className="bg-[#5BA946] hover:bg-[#4a9339] text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger en PDF
          </Button>
        </div>

        {/* Content to be exported as PDF */}
        <div ref={contentRef} className="bg-white rounded-xl shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8 pb-6 border-b border-gray-200">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Key className="w-10 h-10 text-[#5BA946]" />
              <h1 className="text-3xl font-bold text-gray-900">EdoMatch API Keys</h1>
            </div>
            <p className="text-gray-500">Configuration secrets for the EdoMatch CV Assistant</p>
            <p className="text-sm text-gray-400 mt-2">Generated: {new Date().toLocaleDateString('fr-FR', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>

          {/* API Keys List */}
          <div className="space-y-6">
            {apiKeys.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="border border-gray-200 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center`}>
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                      <p className="text-sm text-gray-500">{item.key}</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm break-all text-gray-700">
                    {item.value}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Warning */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm font-medium">⚠️ Security Warning</p>
              <p className="text-amber-700 text-sm mt-1">
                Keep these API keys confidential. Do not share this document publicly or commit it to version control.
                If compromised, regenerate the keys immediately.
              </p>
            </div>
          </div>

          {/* Usage Instructions */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">📋 Usage Instructions</h4>
            <p className="text-gray-600 text-sm mb-2">
              Add these keys to your <code className="bg-gray-200 px-1 rounded">backend/.env</code> file:
            </p>
            <pre className="bg-gray-800 text-green-400 rounded-lg p-3 text-xs overflow-x-auto">
{`MONGO_URL="${apiKeys[0].value}"
EMERGENT_LLM_KEY="${apiKeys[1].value}"
GOOGLE_GEMINI_API_KEY="${apiKeys[2].value}"`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
